import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import v2Api from '../../../../utils/v2Api';

const TestSessionContext = createContext(null);

export const useTestSession = () => {
    const ctx = useContext(TestSessionContext);
    if (!ctx) throw new Error('useTestSession must be used within TestSessionProvider');
    return ctx;
};

// ── localStorage helpers ──
const getLocalStorageKey = (sessionId) => `test_session_${sessionId}_answers`;

const saveToLocalStorage = (sessionId, answersMap) => {
    if (!sessionId) return;
    try {
        const data = [];
        answersMap.forEach((val, qId) => {
            data.push({ question_id: qId, answer_text: val.answer_text, flagged: val.flagged });
        });
        localStorage.setItem(getLocalStorageKey(sessionId), JSON.stringify({ answers: data, _timestamp: Date.now() }));
    } catch { /* quota exceeded — silent fail, server auto-save is primary */ }
};

const loadFromLocalStorage = (sessionId) => {
    if (!sessionId) return null;
    try {
        const raw = localStorage.getItem(getLocalStorageKey(sessionId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { answers: parsed.answers || [], timestamp: parsed._timestamp || 0 };
    } catch { return null; }
};

const clearLocalStorage = (sessionId) => {
    if (!sessionId) return;
    localStorage.removeItem(getLocalStorageKey(sessionId));
};

// ── Auto-save constants ──
const DEBOUNCE_MS = 2000; // save 2s after last keystroke

// `api` (optional) injects the save/submit transport. Default = the authed
// student endpoints (/sessions/:id/...). The placement taker flow passes an
// auth-less token-based adapter so the SAME engine drives an account-less test.
// `readOnly` (review mode) makes every answer mutator a no-op — the renderers
// stay untouched but clicks/typing can never change the pre-filled answers.
export const TestSessionProvider = ({ children, api = null, readOnly = false }) => {
    const [session, setSession] = useState(null);
    const [test, setTest] = useState(null);
    const [courseTest, setCourseTest] = useState(null);
    const [answers, setAnswers] = useState(new Map()); // Map<questionId, { answer_text, flagged, dirty }>
    const [choiceGroupSelections, setChoiceGroupSelectionsState] = useState({});
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [saveError, setSaveError] = useState(null); // null | 'auth' | 'network'
    const saveTimerRef = useRef(null);
    const autoSaveTimerRef = useRef(null);
    const savingRef = useRef(false);
    // The latest answers, readable from inside an in-flight save whose closure is stale.
    const answersRef = useRef(answers);
    useEffect(() => { answersRef.current = answers; }, [answers]);
    // A save was asked for while another was in flight → run once more when it returns.
    const pendingSaveRef = useRef(false);
    const saveRef = useRef(null);

    const isDirty = Array.from(answers.values()).some(a => a.dirty);

    const setAnswer = useCallback((questionId, text) => {
        if (readOnly) return;
        setAnswers(prev => {
            const next = new Map(prev);
            const existing = next.get(questionId) || { answer_text: '', flagged: false, dirty: false };
            next.set(questionId, { ...existing, answer_text: text, dirty: true });
            return next;
        });
    }, [readOnly]);

    const toggleFlag = useCallback((questionId) => {
        if (readOnly) return;
        setAnswers(prev => {
            const next = new Map(prev);
            const existing = next.get(questionId) || { answer_text: '', flagged: false, dirty: false };
            next.set(questionId, { ...existing, flagged: !existing.flagged, dirty: true });
            return next;
        });
    }, [readOnly]);

    const setChoiceGroupSelection = useCallback((groupNum, questionId) => {
        if (readOnly) return;
        setChoiceGroupSelectionsState(prev => ({ ...prev, [groupNum]: questionId }));
    }, [readOnly]);

    // Write to localStorage whenever answers change
    useEffect(() => {
        if (session?.id && answers.size > 0) {
            saveToLocalStorage(session.id, answers);
        }
    }, [answers, session?.id]);

    // Hydrate from API response, then merge localStorage backup
    const hydrate = useCallback((data) => {
        setSession(data.session);
        setTest(data.test);
        setCourseTest(data.course_test);

        // Restore any persisted choice_group_selections from session.metadata
        const metaSelections = data.session?.metadata?.choice_group_selections;
        if (metaSelections && typeof metaSelections === 'object') {
            // Metadata may come back with string keys; normalize values to numbers.
            const normalized = {};
            for (const [k, v] of Object.entries(metaSelections)) {
                normalized[k] = typeof v === 'number' ? v : parseInt(v, 10);
            }
            setChoiceGroupSelectionsState(normalized);
        }

        const answerMap = new Map();
        (data.saved_answers || []).forEach(a => {
            answerMap.set(a.question_id, {
                answer_text: a.answer_text || '',
                flagged: a.flagged || false,
                dirty: false,
            });
        });

        // Merge localStorage backup — recover answers that failed to save to server
        // localStorage always wins if its answer is different from server (it's the most recent keystroke data)
        const local = loadFromLocalStorage(data.session?.id);
        if (local && local.answers.length > 0) {
            local.answers.forEach(la => {
                const serverAnswer = answerMap.get(la.question_id);
                const serverText = serverAnswer?.answer_text || '';
                const localText = la.answer_text || '';
                if (localText && localText !== serverText) {
                    answerMap.set(la.question_id, {
                        answer_text: localText,
                        flagged: la.flagged || false,
                        dirty: true, // mark dirty so auto-save pushes to server
                    });
                }
            });
        }

        setAnswers(answerMap);
    }, []);

    // Save dirty answers (no-op in preview mode where session.id is null).
    //
    // Two rules keep an answer from being lost while a request is in flight (seen on dev
    // 2026-10-13: Q14 selected and "Saved" on screen, never on the server until submit):
    //   1. Only the answers this request CARRIED are marked clean, and only if they still hold
    //      the value that was sent. Anything changed meanwhile stays dirty.
    //   2. A save asked for during the flight is not dropped — it runs once the flight returns.
    const save = useCallback(async () => {
        if (!session?.id) return;
        if (savingRef.current) { pendingSaveRef.current = true; return; }
        const dirtyAnswers = [];
        answersRef.current.forEach((val, qId) => {
            if (val.dirty) {
                dirtyAnswers.push({ question_id: qId, answer_text: val.answer_text, flagged: val.flagged });
            }
        });
        if (dirtyAnswers.length === 0) return;

        setSaving(true);
        savingRef.current = true;
        try {
            const savePayload = { answers: dirtyAnswers, choice_group_selections: choiceGroupSelections };
            if (api?.save) await api.save(savePayload);
            else await v2Api.put(`/sessions/${session.id}/save`, savePayload);
            setAnswers(prev => {
                const next = new Map(prev);
                for (const sent of dirtyAnswers) {
                    const cur = next.get(sent.question_id);
                    if (cur?.dirty && cur.answer_text === sent.answer_text && cur.flagged === sent.flagged) {
                        next.set(sent.question_id, { ...cur, dirty: false });
                    }
                }
                return next;
            });
            setSaveError(null);
        } catch (err) {
            const status = err.response?.status;
            setSaveError(status === 401 ? 'auth' : 'network');
            console.error('Auto-save failed:', err);
        } finally {
            setSaving(false);
            savingRef.current = false;
            if (pendingSaveRef.current) {
                pendingSaveRef.current = false;
                // Through the ref so the follow-up sees the latest answers, not this closure's.
                saveRef.current?.();
            }
        }
    }, [session, choiceGroupSelections, api]);
    useEffect(() => { saveRef.current = save; }, [save]);

    // Auto-save: 2s debounce after answer change
    useEffect(() => {
        if (!session?.id || !isDirty) return;
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(() => save(), DEBOUNCE_MS);
        return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
    }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced save (kept for manual use / section navigation)
    const debouncedSave = useCallback(() => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => save(), 500);
    }, [save]);

    // Submit test (no-op in preview mode where session.id is null)
    const submit = useCallback(async (gradingMode) => {
        if (!session?.id || submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const allAnswers = [];
            answers.forEach((val, qId) => {
                allAnswers.push({ question_id: qId, answer_text: val.answer_text, flagged: val.flagged });
            });
            const gradingModeMap = { 'none': 'none', 'immediate': 'immediate', 'delayed': 'default' };
            const grading_mode = gradingMode ? (gradingModeMap[gradingMode] || 'default') : 'default';
            const submitPayload = { answers: allAnswers, grading_mode, choice_group_selections: choiceGroupSelections };
            const res = api?.submit
                ? await api.submit(submitPayload)
                : await v2Api.post(`/sessions/${session.id}/submit`, submitPayload);
            setSession(prev => ({ ...prev, status: 'submitted' }));
            clearLocalStorage(session.id);
            return res.data.data;
        } catch (err) {
            setSubmitError(err.response?.data?.message || 'Submit failed');
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [session, submitting, answers, choiceGroupSelections, api]); // gradingMode is a parameter, not a dependency

    const value = {
        session, test, courseTest, answers, readOnly,
        setAnswer, toggleFlag, choiceGroupSelections,
        setChoiceGroupSelection, currentSectionIndex, setCurrentSectionIndex,
        currentQuestionIndex, setCurrentQuestionIndex,
        isDirty, save, debouncedSave, submit,
        saving, submitting, submitError, saveError, setSaveError,
        hydrate, setSession,
    };

    return (
        <TestSessionContext.Provider value={value}>
            {children}
        </TestSessionContext.Provider>
    );
};

export default TestSessionContext;
